/** Who gets the automatic emails (settings.email). Admin edits; Manager can see. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, errorText } from '../../lib/api';
import { Button, Card, ErrorText, Field, Input } from '../../components/ui';

interface Recipients {
  serviceManagerEmails: string[];
  ownerEmails: string[];
  closeRecipients: string[];
}

const FIELDS: { key: keyof Recipients; label: string; hint: string }[] = [
  { key: 'closeRecipients', label: 'Job package (billing)', hint: 'Gets the form PDFs, scans and office-only notes when a job closes. Empty = not sent.' },
  { key: 'serviceManagerEmails', label: 'Service manager', hint: 'Additional issues, PIN lockouts, failed emails, daily digest. Empty = every active Manager.' },
  { key: 'ownerEmails', label: 'Owner', hint: 'Backups, audit chain, restore tests, weekly report. Empty = the alert address of the server.' },
];

const toList = (s: string) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

export function RecipientsCard({ isAdmin }: { isAdmin: boolean }) {
  const q = useQuery({ queryKey: ['email-recipients'], queryFn: () => api.get<Recipients>('/api/email/recipients') });
  if (!q.data) return null;
  return <RecipientsForm key={q.dataUpdatedAt} r={q.data} isAdmin={isAdmin} onSaved={() => void q.refetch()} />;
}

function RecipientsForm({ r, isAdmin, onSaved }: { r: Recipients; isAdmin: boolean; onSaved: () => void }) {
  const [f, setF] = useState<Record<keyof Recipients, string>>({
    closeRecipients: r.closeRecipients.join(', '),
    serviceManagerEmails: r.serviceManagerEmails.join(', '),
    ownerEmails: r.ownerEmails.join(', '),
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const save = async () => {
    setError(null);
    try {
      await api.put('/api/email/recipients', { closeRecipients: toList(f.closeRecipients), serviceManagerEmails: toList(f.serviceManagerEmails), ownerEmails: toList(f.ownerEmails) });
      setSaved(true);
      onSaved();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Card title="Who gets the automatic emails" actions={isAdmin && <Button variant="secondary" onClick={save}>{saved ? 'Saved ✓' : 'Save'}</Button>}>
      <ErrorText error={error} />
      <div className="grid gap-3 md:grid-cols-3">
        {FIELDS.map((x) => (
          <Field key={x.key} label={x.label} hint={x.hint}>
            <Input value={f[x.key]} disabled={!isAdmin} placeholder="name@example.com, …" onChange={(e) => { setSaved(false); setF({ ...f, [x.key]: e.target.value }); }} />
          </Field>
        ))}
      </div>
    </Card>
  );
}
